import React, { useEffect, useMemo } from 'react';

import { orderBy } from 'lodash';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';

import { productTitle } from '@src/components/products';
import { GroupedDropdown } from '@src/components/ui';
import { Dropdown } from '@src/components/ui/Dropdown';
import { storeActions } from '@src/ducks';
import { fetchDropdownProducts } from '@src/requests/fetchProducts';
import {
    getDropdownProductsData,
    getProducts,
    isProductsLoading,
} from '@src/selectors/products';
import { unique } from '@src/utils/componentHelpers';


/**
 * ProductsDropdown Component
 *
 * A dropdown component for selecting products, with support for external data
 * or Redux-managed data fetching. Displays products grouped by product line,
 * with proper formatting and handles loading states.
 *
 * @component
 * @param {string} [props.selectedProduct] - Currently selected product slug
 * @param {Immutable.Map} [props.productsData] - Products data (from Redux or external)
 * @param {Function} [props.onChange] - Callback when selection changes
 * @returns {React.ReactElement} - Products dropdown component
 *
 * @example
 * // With Redux data
 * <ProductsDropdown
 *   selectedProduct="product-1"
 *   onChange={(productSlug) => handleChange(productSlug)}
 * />
 *
 * @example
 * // With external data
 * <ProductsDropdown
 *   productsData={immutableProductsData}
 *   selectedProduct="product-1"
 *   onChange={(productSlug) => handleChange(productSlug)}
 * />
 */
const ProductsDropdown = ({
    selectedProduct,
    productsData: productsDataProp,
    onChange,
}) => {
    const dispatch = useDispatch();
    const storeProductsData = useSelector(getDropdownProductsData);
    const productsData = productsDataProp || storeProductsData;
    const externalData = Boolean(productsDataProp);

    const actions = useMemo(() => bindActionCreators({
        ...storeActions,
    }, dispatch), [dispatch]);

    // Generate unique fetch ID for API deduplication
    const fetchId = React.useRef(unique());

    /**
     * Handle product selection change
     * Converts Dropdown.ALL_OPTION to null and calls onChange callback
     */
    const handleChange = React.useCallback((value) => {
        if (value === Dropdown.ALL_OPTION) {
            value = null;
        }

        if (onChange) {
            onChange(value);
        }
    }, [onChange]);

    // Fetch products on mount (only if not using external data)
    useEffect(() => {
        if (!externalData) {
            actions.appRequest(fetchDropdownProducts(fetchId.current));
        }
    }, [externalData, actions]);

    // Transform products data for grouped dropdown consumption
    const products = React.useMemo(() => {
        const productsList = getProducts(productsData).toJS();

        const groups = [
            {
                name: 'Foremost 5.0 (Agent360)',
                items: productsList.filter((p) => p.product_line === 'Foremost 5.0 Agent360'),
            },
            {
                name: 'Foremost Signature (Agent360)',
                items: productsList.filter((p) => p.platform === 'agent360' && p.product_line === 'Foremost Signature Agent360'),
            },
            {
                name: 'Foremost Signature (ARS)',
                items: productsList.filter((p) => p.platform === 'ars'),
            },
        ];

        // Identify any "Other" products that don't fit the above groups
        const categorizedSlugs = new Set();
        groups.forEach((g) => g.items.forEach((p) => categorizedSlugs.add(p.slug)));
        const otherProducts = productsList.filter((p) => !categorizedSlugs.has(p.slug));

        if (otherProducts.length > 0) {
            groups.push({
                name: 'Other',
                items: otherProducts,
            });
        }

        return groups
            .filter((group) => group.items.length > 0)
            .map((group) => ({
                group: group.name,
                items: orderBy(group.items, (p) => productTitle(p, { noHtml: true }))
                    .map((product) => ({
                        id: product.slug,
                        title: productTitle(product),
                    })),
            }));
    }, [productsData]);

    return (
        <GroupedDropdown
            className="products-dropdown"
            defaultSelection="Product"
            title="Product"
            groupedList={products}
            isLoading={isProductsLoading(productsData)}
            selectedOption={selectedProduct}
            onChange={handleChange}
        />
    );
};

ProductsDropdown.propTypes = {
    selectedProduct: PropTypes.string,
    productsData: ImmutablePropTypes.map,
    onChange: PropTypes.func,
};

export default React.memo(ProductsDropdown);
